
import { Body0, Meta1 } from "@/components/ui/Typography";
import { useCurrency } from "@/context/CurrencyContext";
import i18n from "@/i18n";
import { BaseRow, ClassRow, ProfitAndLossData } from "@/types/profit-and-loss-api";
import { useTranslation } from "react-i18next";

const gridColumns = "grid grid-cols-[3fr_2fr_2fr_1fr_1fr]";
const gridGapX = "gap-x-6";

interface ComparisonParts {
    previousText: string;
    percentText: string;
    diffText: string;
    isNegative: boolean;
}

const getComparisonParts = (
    previous: number,
    current: number,
    percent: number,
    formatNumber: (value: number) => string
): ComparisonParts => {
    const sign = percent > 0 ? "+" : "";
    const percentText = `${sign}${percent.toFixed(1)}%`;
    const rawDiff = current - previous;
    const diffSign = rawDiff > 0 ? "+" : rawDiff < 0 ? "-" : "";
    const diffText = `${diffSign}${formatNumber(Math.abs(rawDiff))}`;
    return {
        previousText: formatNumber(previous),
        percentText,
        diffText,
        isNegative: percent < 0
    };
};

const ComparisonValue = ({
    value,
    isNegative
}: {
    value: string;
    isNegative: boolean;
}) => (
    <span className={isNegative ? "text-red-500" : "text-green-600"}>
        {value}
    </span>
);

const ItemRow = ({ item }: { item: BaseRow }) => {
    const { formatNumber } = useCurrency();
    const locale = i18n.language;
    const { title, total, totalPreviousPeriod, differencePercentage } = item;

    const { previousText, percentText, diffText, isNegative } =
        getComparisonParts(totalPreviousPeriod, total, differencePercentage, formatNumber);

    return (
        <>
            <Meta1 className="text-left pl-3">{title[locale]}</Meta1>
            <Meta1 className="text-right">{formatNumber(total)}</Meta1>
            <Meta1 className="text-right">{previousText}</Meta1>
            <Meta1 className="text-right"><ComparisonValue value={percentText} isNegative={isNegative} /></Meta1>
            <Meta1 className="text-right pr-3"><ComparisonValue value={diffText} isNegative={isNegative} /></Meta1>
        </>
    );
};

const SectionBlock = ({ row }: { row: ClassRow | BaseRow }) => {
    const { formatNumber } = useCurrency();
    const locale = i18n.language;
    const { title, total, totalPreviousPeriod, differencePercentage } = row;
    const subRows = "subRows" in row ? row.subRows : [];

    const { previousText, percentText, diffText, isNegative } =
        getComparisonParts(totalPreviousPeriod, total, differencePercentage, formatNumber);

    return (
        <div className="mb-6">
            <div className={`${gridColumns} ${gridGapX} gap-y-1 mb-2 items-center`}>
                <Body0 className="text-left uppercase tracking-wide" as="div"> {title[locale]} </Body0>
                <Body0 className="text-right" as="div">{formatNumber(total)}</Body0>
                <Body0 className="text-right" as="div">{previousText}</Body0>
                <Body0 className="text-right" as="div"><ComparisonValue value={percentText} isNegative={isNegative} /></Body0>
                <Body0 className="text-right pr-3" as="div"><ComparisonValue value={diffText} isNegative={isNegative} /></Body0>
            </div>
            {subRows.length > 0 && (
                <div className={`${gridColumns} ${gridGapX} gap-y-1`}>
                    {subRows.map((item) => (
                        <ItemRow key={item.key} item={item} />
                    ))}
                </div>
            )}
        </div>
    );
};

const FinancialSummary = ({
    data,
    startDate,
    endDate
}: {
    data: ProfitAndLossData;
    startDate: string;
    endDate: string;
}) => {
    const { t } = useTranslation();

    if (!data || !data.classRows) {
        return (
            <div className="flex items-center justify-center h-40 text-gray-500 text-base font-medium">
                {t("no_data")}
            </div>
        );
    }

    const allRows: (ClassRow | BaseRow)[] = [
        ...Object.values(data.classRows),
        data.grossProfit,
        data.ebit,
        data.profitBeforeTax
    ];
    const sortedRows = allRows.slice().sort((a, b) => a.index - b.index);

    return (
        <div className="bg-white rounded-2xl w-full px-6 sm:px-8 py-5 shadow-sm overflow-x-auto">
            <div className="w-full text-sm">
                <div className={`${gridColumns} ${gridGapX} mb-3`}>
                    <div />
                    <Meta1 className="text-right">{endDate}</Meta1>
                    <Meta1 className="text-right">{startDate}</Meta1>
                    <div />
                    <div className="pr-3" />
                </div>
                {sortedRows.map((row) => (
                    <SectionBlock key={row.key} row={row} />
                ))}
            </div>
        </div>
    );
};

export default FinancialSummary;
